import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Tabs, Tab, Box, Typography, IconButton, Tooltip } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useContacts } from "../../../hooks/crm/useContacts";


import { colorAdjustedProductType, colorProductType } from "../../../utils/colorEnumFormatter";
import { LOCAL_STORAGE_KEYS } from "../../../services/local_storage_keys";

import PolicyMetadata from "../Cards/PolicyMetadata";
import DepositsBarChartByContributionType from "../Charts/DepositsBarChartByContributionType";
import PensionFundCoverageSummary from "./Metrics/PensionFundCoverageSummary";
import PensionFundCoverageMetadata from "./Cards/PensionFundCoverageMetadata";
import BasicInsuranceDetailsCard from "./Cards/BasicInsuranceDetailsCard";
import InsuranceCoverageMetadata from "./Cards/InsuranceCoverageMetadata";
import PensionFundCoverageCosts from "./Cards/PensionFundCoverageCosts";

import LoansCard from "../Cards/LoansCard";

import PremiumPeriodInfoCard from "./Cards/PremiumPeriodInfoCard";
import PensionDataDisplay from "../PensionDataDisplay";
import RetirementSavingsByAgeTable from "../Tabels/RetirementSavingsByAgeTable";
import PensionFundTable from "../Tabels/PensionFundTable";
import FundActionTable from "../FundActionTable";
import FundTransferHistory from "../FundTransferHistory";

import StyledDrawer from "../../../shared/UIParts/StyledDrawer";
import BeneficiaryChips from "../../Pension/BeneficiaryChips";
import { MimshakService } from "../../../services/mimshakService";
import { formatProductTypeExtended, formatPolicyStatus, formatAdjustedProductType } from "../../../utils/enumFormatter";
import { EmploymentTypeChip } from "../Chips/EmploymentTypeChip";
import { formatHandlerCode, formatIsoDate, formatFullName } from "../../../utils/namesFormater";
import { DepositCollectionDetailsSummary } from "../Cards/DepositCollectionDetailsSummary";
import { LiquiditySummary } from "../LiquiditySummary";
import { PolicyPaths } from "../PolicyPathStrips";
import AccumulatedSavingsTable from "../Tabels/AccumulatedSavingsTable";
import { ManagementFeeDetailsTable } from "../Tabels/ManagementFeeDetailsTable";
import { ProductDebtTab } from "./Cards/DebtDetailCard";
import ProductInsuranceCostsSummary from "./Cards/ProductInsuranceCostsSummary";
import { ProductInsuranceFeeSummary } from "./Cards/ProductInsuranceFeeSummary";
import { ProductPremiumsStackedChart } from "./Charts/ProductPremiumsStackedChart";
import { Product, AdjustedProductType, ProductType } from "../../../interfaces/DTO/mislaka_xml_models";
import { PolicyEmploymentStatus } from "../../../interfaces/DTO/mislaka_xml_pension_models";
import { MimshakProductUtil } from "../../../services/MimshakProductUtil";
import PensionSummaryCards from "../../Pension/metrics/PensionSummaryCards";
import { ProductDepositsYearSummary } from "../Cards/ProductDepositsYearSummary";
import { PolicyStatuses } from "../Chips/PolicyStatuses";
import ScatterFundYieldChart from "../Charts/ScatterFundYieldChart";
import WeightedProductYieldsSummary from "../Cards/AverageFundYieldsSummary";
import RetirementSavingsByAgeCards from "../Cards/RetirementSavingsByAgeCards";
import LastDepositSummaryDisplay from "./Cards/LastDepositSummary";
import OpenClientInProviderButton from "./OpenClientInProviderButton";
import LoginAsClientButton from "./LoginAsClientButton";

// אפשר לייבא את Product, Policy וכו' במידת הצורך
const PolicyHistoryDrawer = lazy(() => import("../Tabels/PolicyHistoryDrawer"));

interface InsuranceProductDrawerProps {
  product: Product | null;
  onClose: () => void;
  zIndex?: number; // למשל כשהמגירה נפתחת מתוך דיאלוג
  /** Contact of the product — when omitted it is resolved by the client's ID number (client_id ↔ israeli_id) */
  contactId?: number;
}
const TAB_ORDER = [
   
  "paths",
  "comparepaths",
  "deposits",
  "policy",
  "balancetypes",
  "pension",
  "fees",
  "premiumperiods",
   "debts",
  "collection",
  "pensiondetails",
  "basicinsurance",
  "insurancecoverage",
  "coverage", 
  "loans",
   
  "transferout"
];
type TabType = { key: string; label: string; content: React.ReactNode };
const InsuranceProductDrawer: React.FC<InsuranceProductDrawerProps> = ({ product, onClose, zIndex, contactId }) => {
  const { tabs, initialIndex, latestUpdateDate } = useMemo(() => {
    if (!product) return { tabs: [], initialIndex: 0, latestUpdateDate: null };
const pathCodes = MimshakService.extractPathCodesFromProducts([product]) || [];

    // חישוב סכומים לפי fund_id
    const fundAmountMap = MimshakService.aggregateAmountsByFundIdFromProducts([product]);

    // חישובים ממחלקת utility
    const totalInsuranceCoverages = MimshakProductUtil.countInsuranceCoverages(product);
    const totalinvestmentPaths = MimshakProductUtil.countInvestmentPaths(product);
    const latestUpdateDate = MimshakService.getLatestUpdateDate(product.policies_or_accounts);
    const totalDepositMonths = MimshakService.getDepositPeriodsCountForLast12MonthsFromProducts([product], latestUpdateDate);
    const totalPremiumPeriods = MimshakProductUtil.countPremiumPeriods(product);
    const totalPensionCoverages = MimshakProductUtil.countPensionCoverages(product);
    const totalLoans = MimshakProductUtil.countLoans(product);
    const allDebtsCount = MimshakProductUtil.countDebts(product);
    const totalBasicInsurance = MimshakProductUtil.countBasicInsurance(product);
    const hasPensionDetails = MimshakProductUtil.hasPensionDetails(product);
    const hasPensionData = MimshakProductUtil.hasPensionData(product);
    const hasPathCode = MimshakProductUtil.hasPathCode(product);

    // טאבים פוטנציאליים
   const allTabs: Record<string, TabType | undefined> = {
      basicinsurance: totalBasicInsurance > 0 && {
        key: "basicinsurance",
        label: `פרטי ביטוח יסודי (${totalBasicInsurance})`,
        content: (
          <>
            <BasicInsuranceDetailsCard product={product} />
          </>
        ),
      },
      insurancecoverage: totalInsuranceCoverages > 0 && {
        key: "insurancecoverage",
        label: `כיסויים ביטוחיים (${totalInsuranceCoverages})`,
        content: (
          <>
          
              <InsuranceCoverageMetadata  product={product} />
        
          </>
        ),
      },
      coverage: totalPensionCoverages > 0 && {
        key: "coverage",
        label: `כיסויים פנסיוניים (${totalPensionCoverages})`,
        content: (
          <>
            <PensionFundCoverageSummary policies={product.policies_or_accounts} />
            {product.policies_or_accounts.map((policy, policyIndex) => (
              <PensionFundCoverageMetadata key={policyIndex} policy={policy} />
            ))}
          </>
        ),
      },
      policy: {
        key: "policy",
        label: "פרטי פוליסה",
        content: <PolicyMetadata product={product} />,
      },
      balancetypes: {
        key: "balancetypes",
        label: "סוגי יתרות",
        content: (
          <>
            {product.policies_or_accounts.map((policy, policyIndex) => (
              <React.Fragment key={policyIndex}>
                <AccumulatedSavingsTable policy={policy} />
                <ManagementFeeDetailsTable policy={policy} />
              </React.Fragment>
            ))}
          </>
        ),
      },
     paths: totalinvestmentPaths > 0
      ? {
          key: "paths",
          label: `מסלולים (${totalinvestmentPaths})`,
          content: <PolicyPaths policies={product.policies_or_accounts} />,
        }
      : undefined,
      comparepaths: hasPathCode && {
        key: "comparepaths",
        label: "השוואת מסלולים",
       content: (
          <>
            <WeightedProductYieldsSummary products={[product]} title="תשואה שנתית משוקללת" />
            <Box mt={2}>
              <ScatterFundYieldChart
                productType={
                  product.details?.adjusted_product_type === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
                  product.details?.adjusted_product_type === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE
                    ? ProductType.LIFE_INSURANCE_SAVINGS
                    : product.details?.product_type
                }
                highlightedFunds={pathCodes.map(fundId => ({
                  fundId,
                  amount: fundAmountMap.get(fundId)
                }))}
                autoSelectHighlighted={true}
                includeIRA={true}
              />
            </Box>
          </>
        ),
      },
      deposits: {
        key: "deposits",
        label: `הפקדות (${totalDepositMonths})`,
           content: (
        <> <DepositsBarChartByContributionType policies={product.policies_or_accounts} /> 
        <ProductDepositsYearSummary product={product} /></>),
      },
      fees: {
        key: "fees",
        label: `עלויות`,
        content: (
          <>
            {product.policies_or_accounts.map((policy, policyIndex) => (
              <PensionFundCoverageCosts key={policyIndex} policy={policy} />
            ))}
            <ProductInsuranceFeeSummary product={product} />
            <ProductPremiumsStackedChart product={product} />
            {product.policies_or_accounts.map((policy, policyIndex) => (
              <ManagementFeeDetailsTable key={policyIndex} policy={policy} />
            ))}
            <ProductInsuranceCostsSummary product={product} />
          </>
        ),
      },
      collection: {
      key: "collection",
      label: "גבייה",
      content: (
        <>
          <DepositCollectionDetailsSummary policies={product.policies_or_accounts} />
          
          {product.policies_or_accounts?.map((policy, index) => (
            policy.last_deposit_summary?.length > 0 && (
              <div key={index}>
                <h4>סיכום הפקדה אחרונה - פוליסה {index + 1}</h4>
                {policy.last_deposit_summary.map((summary, idx) => (
                  <LastDepositSummaryDisplay key={idx} summary={summary} />
                ))}
              </div>
            )
          ))}
        </>
      ),
    },
      premiumperiods: totalPremiumPeriods > 0 && {
        key: "premiumperiods",
        label: `פרטי פרמיות (${totalPremiumPeriods})`,
        content: (
          <>
            {product.policies_or_accounts.flatMap((policy, policyIndex) =>
              policy.insurence_coverages?.map((coverage, coverageIndex) => (
                <PremiumPeriodInfoCard key={`${policyIndex}-${coverageIndex}`} coverage={coverage} />
              )) ?? []
            )}
          </>
        ),
      },
      loans: totalLoans > 0 && {
        key: "loans",
        label: `הלוואות (${totalLoans})`,
        content: <LoansCard product={product} />,
      },
      debts: allDebtsCount > 0 && {
        key: "debts",
        label: `חובות (${allDebtsCount})`,
        content: <ProductDebtTab policies={product.policies_or_accounts} />,
      },
      pension: hasPensionData && {
        key: "pension",
        label: `פרטי פנסיה`,
        content: (
          <>
           
            {product.policies_or_accounts.map((policy, policyIndex) => (
              <PensionDataDisplay key={policyIndex} pensionData={policy.pension_data} />
            ))}
            {product.policies_or_accounts.map((policy, policyIndex) => (
              <RetirementSavingsByAgeCards key={policyIndex} data={policy.retirement_savings_by_age} />
            ))}
            {/* {product.policies_or_accounts.map((policy, policyIndex) => (
              <PensionFundTable key={policyIndex} data={policy.pension_fund_details} />
            ))} */}
          </>
        ),
      },
      pensiondetails: hasPensionDetails && {
        key: "pensiondetails",
        label: `פרטי קצבה`,
        content: (
          <>
            <PensionSummaryCards products={[product]} />
            {/* {product.policies_or_accounts.map((policy, policyIndex) => (
              <RetirementSavingsByAgeCards key={policyIndex} data={policy.retirement_savings_by_age} />
            ))} */}
             {product.policies_or_accounts.map((policy, policyIndex) => (
              <PensionFundTable key={policyIndex} data={policy.pension_fund_details} />
            ))}
          </>
        ),
      },

      transferout: {
        key: "transferout",
        label: `ניודים ופדיון כספים`,
        content: (
          <>
            <FundActionTable fund_actions={product.policies_or_accounts[0]?.fund_actions ?? []} />
            <FundTransferHistory deposits={product.policies_or_accounts[0]?.funds_transfer ?? []} />
            <LiquiditySummary policies={product.policies_or_accounts} />
           
          </>
        ),
      }
    };

    // בניית מערך טאבים מסודר + מסנן החוצה טאבים לא רלוונטיים
   const localTabs: TabType[] = TAB_ORDER
      .map(tabKey => allTabs[tabKey])
      .filter((tab): tab is TabType => Boolean(tab))
      .reverse();


    // שליפת טאב אחרון מה-localStorage אם קיים (ורלוונטי)
    const lastIndex = Math.max(0, localTabs.length - 1);
    const DEFAULT_TAB_KEY = localTabs[lastIndex]?.key ?? "";
    const storedKey = localStorage.getItem(LOCAL_STORAGE_KEYS.insuranceProductDrawerSelectedTab) || DEFAULT_TAB_KEY;
    const index = localTabs.findIndex(tab => tab.key === storedKey);
    const initialIndex = index >= 0 ? index : lastIndex;

    return { tabs: localTabs, initialIndex: initialIndex, latestUpdateDate };
  }, [product]);

  const [tabIndex, setTabIndex] = useState(initialIndex);

  useEffect(() => {
    setTabIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const selectedTabKey = tabs[tabIndex]?.key;
    if (selectedTabKey) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.insuranceProductDrawerSelectedTab, selectedTabKey);
    }
  }, [tabIndex, tabs]);

  // היסטוריית הפוליסה — לפי לקוח + יצרן + מספר פוליסה; הלקוח מזוהה לפי ת"ז אם לא הועבר
  const { data: contacts = [] } = useContacts();
  const historyContactId = useMemo(() => {
    if (contactId) return contactId;
    const clientId = product?.client?.client_id;
    return clientId ? contacts.find((c) => c.israeli_id === clientId)?.id : undefined;
  }, [contactId, contacts, product]);
  // הטלפון לכניסה לאזור האישי של הלקוח: מהמסלקה, ואם אין - מאנשי הקשר
  const clientPhone = useMemo(
    () => product?.client?.cellular || contacts.find((c) => c.id === historyContactId)?.phone,
    [product, contacts, historyContactId]
  );
  const historyPolicyNumber = product?.policies_or_accounts[0]?.policy_number;
  const historyHandlerCode = product?.details?.handler_code;
  const canShowHistory = !!historyContactId && !!historyPolicyNumber && !!historyHandlerCode;
  const [historyOpen, setHistoryOpen] = useState(false);
  // The history drawer (and its chunk) loads on the first click, then stays mounted for its close animation
  const [historyOpened, setHistoryOpened] = useState(false);

  if (!product) return null;

  return (
    <StyledDrawer
      open={!!product}
      onClose={onClose}
      onBack={onClose}
      width={800}
      anchor="left"
      zIndex={zIndex}
    >
      {/* כותרת */}
      <Typography variant="h6" style={{ color: colorAdjustedProductType(product.details?.adjusted_product_type || product.details?.product_type) }}>
        {formatAdjustedProductType(product.details?.adjusted_product_type || product.details?.product_type)}
      </Typography>
      <Typography variant="body2" color="gray">
        יצרן : {formatHandlerCode(product.details?.handler_code) || ""}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" color="gray">
          מספר פוליסה: {product.policies_or_accounts[0]?.policy_number || ""}
        </Typography>
        <OpenClientInProviderButton
          handlerCode={product.details?.handler_code}
          clientId={product.client?.client_id}
          policyNumber={product.policies_or_accounts[0]?.policy_number}
        />
        <LoginAsClientButton
          handlerCode={product.details?.handler_code}
          clientId={product.client?.client_id}
          phone={clientPhone}
          zIndex={zIndex}
        />
      </Box>
      {historyOpened && canShowHistory && (
        <Suspense fallback={null}>
          <PolicyHistoryDrawer
            policy={
              historyOpen
                ? { contactId: historyContactId!, handlerCode: historyHandlerCode!, policyNumber: historyPolicyNumber! }
                : null
            }
            onClose={() => setHistoryOpen(false)}
            // above this drawer, also when this drawer was raised over a dialog
            zIndex={(zIndex ?? 1200) + 1}
          />
        </Suspense>
      )}
     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <PolicyStatuses product={product} showConversionFactors />
        </Typography>
        {product.policies_or_accounts.some(
          (policy) =>
            policy.pension_data &&
            policy.pension_data.end_date_temporary_risk
        ) && (
          <Typography variant="body2" sx={{ color: "orange", fontWeight: "bold" }}>
            תום תוקף ריסק זמני : {formatIsoDate(product.policies_or_accounts[0]?.pension_data?.end_date_temporary_risk || "")}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="body2">
          נכון לתאריך: {formatIsoDate(latestUpdateDate)}
        </Typography>
        {product.policies_or_accounts[0]?.plan_or_account_type && (
          <EmploymentTypeChip employmentType={product.policies_or_accounts[0].plan_or_account_type as PolicyEmploymentStatus} />
        )}
        {canShowHistory && (
          <Tooltip title="היסטוריית הפוליסה">
            <IconButton
              size="small"
              onClick={() => {
                setHistoryOpened(true);
                setHistoryOpen(true);
              }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6">{formatFullName(product)}</Typography>
      
      </Box>
       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      
        <BeneficiaryChips products={[product]} />
      </Box>

      {/* טאבים */}
      <Box dir="ltr">
        <Tabs
          value={tabIndex}
          onChange={(_event, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minWidth: 300,
            "& .MuiTabs-scrollButtons": {
              width: 28,
              visibility: "visible",
              backgroundColor: "green"
            },
            "& .MuiTabs-scrollButtons svg": {
              fontSize: "2rem",
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* תוכן הטאב שנבחר */}
      <Box sx={{ mt: 2 }}>
        {tabs[tabIndex]?.content}
      </Box>
    </StyledDrawer>
  );
};

export default InsuranceProductDrawer;